// src/features/auth/SignUpPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { registerUser, clearAuthStatus } from './authSlice';
import * as authService from '../../api/authService';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next'; // ADDED: Import useTranslation
import {
    Typography, Box, TextField, Button, CircularProgress, Alert, Stepper, Step, StepLabel, Link, Stack, Divider
} from '@mui/material';
import AuthLayout from '../../layout/AuthLayout';
import PasswordField from '../../components/common/PasswordField';
import GoogleAuthButton from '../../components/auth/GoogleAuthButton';
import { validatePassword } from '../../utils/validation'; // UPDATED: Import validation utility

const steps = ['Enter Details', 'Verify Email']; // TODO: Consider moving to i18n if dynamic translation needed
const RESEND_TIMER_SECONDS = parseInt(process.env.REACT_APP_RESEND_CODE_TIMER || '60', 10);

const SignUpPage: React.FC = () => {
    const { t } = useTranslation(); // ADDED: Initialize translation
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { status, error, isAuthenticated } = useAppSelector((state) => state.auth);

    const [activeStep, setActiveStep] = useState(0);
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));

    const [formError, setFormError] = useState<string | null>(null);
    const [verificationStatus, setVerificationStatus] = useState<'idle' | 'loading' | 'failed'>('idle');
    const [verificationError, setVerificationError] = useState<string | null>(null);

    const [countdown, setCountdown] = useState(0);
    const timerRef = useRef<NodeJS.Timeout>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        dispatch(clearAuthStatus());
        return () => {
            dispatch(clearAuthStatus());
            if(timerRef.current) clearInterval(timerRef.current);
        };
    }, [dispatch]);

    useEffect(() => {
        if (countdown > 0) {
            timerRef.current = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [countdown]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleCodeChange = (element: HTMLInputElement, index: number) => {
        if (isNaN(Number(element.value))) return;

        const newCode = [...verificationCode];
        newCode[index] = element.value;
        setVerificationCode(newCode);

        if (element.value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const startRequestProcess = async () => {
        setVerificationStatus('loading');
        setVerificationError(null);
        try {
            await authService.requestEmailVerification(email);
            setCountdown(RESEND_TIMER_SECONDS);
            return true;
        } catch (err: any) {
            setVerificationStatus('failed');
            setVerificationError(t('verification_failed'));
            return false;
        } finally {
            setVerificationStatus('idle');
        }
    }

    const handleRequestCode = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);

        if (!email || !username || !password || !confirmPassword) {
            setFormError(t('all_fields_required'));
            return;
        }
        if (password !== confirmPassword) {
            setFormError(t('passwords_do_not_match'));
            return;
        }

        // UPDATED: Use centralized password validation
        const passwordError = validatePassword(password);
        if(passwordError) {
            setFormError(passwordError);
            return;
        }

        const success = await startRequestProcess();
        if(success) {
            setActiveStep(1);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0) return;
        await startRequestProcess();
    }

    const handleRegister = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const finalCode = verificationCode.join('');
        if (email && username && password && finalCode.length === 6) {
            dispatch(registerUser({ email, username, password, verificationCode: finalCode }));
        } else {
            setFormError(t('invalid_verification_code'));
        }
    };

    const isLoading = status.auth === 'loading' || verificationStatus === 'loading';

    return (
        <AuthLayout>
            <Typography component="h1" variant="h5">{t('signup')}</Typography>

            <Stepper activeStep={activeStep} sx={{ width: '100%', my: 3 }}>
                {steps.map(label => <Step key={label}><StepLabel>{t(label.toLowerCase().replace(' ', '_'))}</StepLabel></Step>)}
            </Stepper>

            {formError && <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>{formError}</Alert>}
            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            {verificationError && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{verificationError}</Alert>}

            {activeStep === 0 && (
                <Box component="form" onSubmit={handleRequestCode} noValidate sx={{ width: '100%' }}>
                    <TextField margin="normal" required fullWidth id="username" label={t('username')} name="username" value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" autoFocus />
                    <TextField margin="normal" required fullWidth id="email" label={t('email_address')} type="email" name="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"/>
                    <PasswordField name="password" label={t('password')} id="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" />
                    <PasswordField name="confirmPassword" label={t('confirm_new_password')} id="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />

                    <Button
                        type="submit" fullWidth variant="contained"
                        sx={{
                            mt: 3, mb: 2,
                            bgcolor: 'inverse.main', color: 'inverse.contrastText', '&:hover': { bgcolor: 'inverse.hover.main' }
                        }}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : t('continue')}
                    </Button>
                </Box>
            )}

            {activeStep === 1 && (
                <Box component="form" onSubmit={handleRegister} noValidate sx={{ mt: 1, width: '100%' }}>
                    <Typography variant="body2" align="center" color="text.secondary">
                        <Trans i18nKey="email_verification_sent" values={{ email }} components={{ strong: <strong /> }} />
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ my: 3 }}>
                        {verificationCode.map((data, index) => (
                            <TextField
                                key={index}
                                sx={{ width: '40px' }}
                                inputRef={el => inputRefs.current[index] = el}
                                value={data}
                                onChange={e => handleCodeChange(e.target as HTMLInputElement, index)}
                                onKeyDown={e => handleKeyDown(e, index)}
                                inputProps={{
                                    maxLength: 1,
                                    style: { textAlign: 'center', fontSize: '1.2rem' }
                                }}
                            />
                        ))}
                    </Stack>

                    <Button type="submit" fullWidth variant="contained"
                            sx={{ mt: 3, mb: 2, bgcolor: 'inverse.main', color: 'inverse.contrastText', '&:hover': { bgcolor: 'inverse.hover.main' } }}
                            disabled={isLoading}>
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : t('verify_and_sign_up')}
                    </Button>

                    <Box textAlign="center" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Link component="button" variant="body2" onClick={() => setActiveStep(0)} color="inherit" type="button">
                            {t('change_email')}
                        </Link>
                        <Button onClick={handleResendCode} disabled={countdown > 0} size="small">
                            {countdown > 0 ? t('resend_in_seconds', { countdown }) : t('resend_code')}
                        </Button>
                    </Box>
                </Box>
            )}

            <Divider sx={{ my: 2, width: '100%' }}>{t('or')}</Divider>
            <GoogleAuthButton />

            <Box textAlign="center" sx={{ mt: 1 }}>
                <Link component={RouterLink} to="/login" color="inherit">
                    {t('back_to_sign_in')}
                </Link>
            </Box>
        </AuthLayout>
    );
};

export default SignUpPage;